/**
 * GazeObjectMatcher — combines gaze zone/target with detected object
 * bounding boxes to determine WHAT the student is looking at.
 *
 * If the gaze projection target point falls inside (or near) a detected
 * object's bounding box, we flag it as "student is looking at [object]".
 */

import type { Detection } from '../hooks/useObjectDetector';

export interface GazeObjectMatch {
  detection: Detection;
  /** Distance from gaze target to bbox center, normalized [0, 1]. */
  distance: number;
  /** True if gaze target is inside or within proximity of the bbox. */
  isLookingAt: boolean;
}

/**
 * Compute the gaze target point in normalized [0, 1] coordinates from
 * the same projection math used in the overlay drawing.
 */
export function computeGazeTarget(
  irisLeftNorm: { x: number; y: number },
  irisRightNorm: { x: number; y: number },
  yawDeg: number,
  pitchDeg: number,
): { x: number; y: number } {
  const midX = (irisLeftNorm.x + irisRightNorm.x) / 2;
  const midY = (irisLeftNorm.y + irisRightNorm.y) / 2;
  const yawRad = (yawDeg * Math.PI) / 180;
  const pitchRad = (pitchDeg * Math.PI) / 180;

  const baseDist = 0.35;
  const pitchFactor = 1 + Math.min(1, Math.max(0, -pitchDeg / 30));
  const projLen = baseDist * pitchFactor;

  return {
    x: midX + Math.sin(yawRad) * projLen * 0.8,
    y: midY + Math.abs(Math.cos(pitchRad)) * projLen,
  };
}

/**
 * Match gaze target against detected objects. Returns matches sorted by
 * distance (closest first).
 *
 * @param gazeTarget Normalized gaze target point [0, 1]
 * @param detections Object detections with normalized bboxes
 * @param proximityThreshold How close (normalized) the gaze must be to a
 *        bbox edge to count as "looking at" (default 0.08 = ~8% of frame)
 */
export function matchGazeToObjects(
  gazeTarget: { x: number; y: number },
  detections: Detection[],
  proximityThreshold = 0.08,
): GazeObjectMatch[] {
  const matches: GazeObjectMatch[] = [];

  for (const det of detections) {
    const bb = det.bboxNorm;
    const cx = bb.x + bb.w / 2;
    const cy = bb.y + bb.h / 2;

    // Distance from gaze target to bbox center
    const dx = gazeTarget.x - cx;
    const dy = gazeTarget.y - cy;
    const distance = Math.sqrt(dx * dx + dy * dy);

    // Check if gaze target is inside the bbox (with proximity buffer)
    const inside =
      gazeTarget.x >= bb.x - proximityThreshold &&
      gazeTarget.x <= bb.x + bb.w + proximityThreshold &&
      gazeTarget.y >= bb.y - proximityThreshold &&
      gazeTarget.y <= bb.y + bb.h + proximityThreshold;

    matches.push({
      detection: det,
      distance,
      isLookingAt: inside,
    });
  }

  matches.sort((a, b) => a.distance - b.distance);
  return matches;
}

/** Get the salience-color for drawing bounding boxes. */
export function salienceColor(salience: Detection['salience']): string {
  switch (salience) {
    case 'high': return '#ef4444';
    case 'low':  return '#eab308';
    case 'none': return '#22c55e';
  }
}
